import React, { useMemo } from "react";
import {
  getOrgName,
  resetOrganizationOverride,
  clearOrgNameCache,
} from "../../config/organization";
import { authApi } from "@/api/services";
import { useApiToken } from "@/components/providers/token-provider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RefreshCcw } from "lucide-react";

const ORGANIZATIONS = [
  "stormimpact-dev",
  "urbint-west",
  "urbint-east",
  "aep",
  "southern-company",
  "firstenergy",
  "oge",
  "ocala",
  "dlc"
];

export const DevOrgSwitcher: React.FC = () => {
  const currentOrg = getOrgName();
  const { isAuthenticated } = useApiToken();

  const isVisible = useMemo(() => {
    const hostname = window.location.hostname;

    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.startsWith("stormimpact-dev.") ||
      currentOrg === "stormimpact-dev"
    );
  }, [currentOrg]);

  if (!isVisible) return null;

  const switchToNewOrgLogin = (newOrg: string) => {
    localStorage.setItem("stormimpact_local_org", newOrg);
    // getOrgName() caches at module load and feeds the x-org-name request
    // header (see config/organization.ts + api/client.ts). Drop the cache so
    // any pre-navigation read resolves to newOrg.
    clearOrgNameCache();
    window.location.href = `/login?org=${encodeURIComponent(newOrg)}`;
  };

  const handleOrgChange = async (newOrg: string) => {
    if (newOrg === currentOrg) return;

    // No live session to tear down (e.g. sitting on /login). Logging out here
    // would fire /auth/logout with the x-org-name of an org we never
    // authenticated against — and for a realm that may not exist locally,
    // which is exactly how a dev "switch" turns into a Keycloak 404. Just
    // point the browser at the new org's login.
    if (!isAuthenticated) {
      switchToNewOrgLogin(newOrg);
      return;
    }

    // Authenticated: the session is HttpOnly cookies (si_at/si_rt) plus a live
    // Keycloak session — none of which JS can clear. Hit the real logout
    // endpoint FIRST, while getOrgName() still resolves to the CURRENT org, so
    // the backend revokes the right realm's refresh token (back-channel,
    // terminating the KC session). Logout returns no end-session URL.
    try {
      await authApi.logout();
    } catch (error) {
      // Backend clears cookies even on error paths; fall through to a full
      // page load of the new org's login regardless.
      console.warn("Org switch logout failed:", error);
    }

    // Persist target org, drop the cached org name, and full-page load the new
    // org's login (identical to the unauthenticated path above).
    switchToNewOrgLogin(newOrg);
  };

  return (
    <div className="flex items-center gap-2 mr-2">
      <span className="text-[10px] font-bold text-white uppercase tracking-wider hidden lg:inline">
        Dev Mode
      </span>
      <Select value={currentOrg} onValueChange={handleOrgChange}>
        <SelectTrigger className="h-8 w-[180px] bg-white/10 border-white/20 text-white text-xs hover:bg-white/20 transition-colors">
          <SelectValue placeholder="Switch Org" />
        </SelectTrigger>
        <SelectContent>
          {ORGANIZATIONS.map((org) => (
            <SelectItem key={org} value={org} className="text-xs">
              {org}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10"
        onClick={resetOrganizationOverride}
        title="Reset to Default"
      >
        <RefreshCcw className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

export default DevOrgSwitcher;
